/**
 * Etiquetas de medio de pago para analytics (assessment §9.5).
 * `payment_type` es el nombre técnico del medio; `payment_box` es el texto que ve el usuario.
 * ENA-1803 (Firebase) reutiliza las mismas etiquetas: no arma strings propios.
 */
import type { PaymentMethodItem } from '../components/PaymentMethodCarousel/types';
import { translate } from '../i18n';
import type { PaymentIssuer } from './paymentAnalytics';

export interface PaymentAnalyticsLabels {
  payment_type: string;
  payment_box: string;
  /** Solo la tarjeta de crédito Carrefour; ausente en cualquier otro medio. */
  payment_issuer?: PaymentIssuer;
}

/** Acuerdo §9.5: no enviar el enum `MERCADOPAGO`. En VTEX histórico el equivalente era `MercadoPagoPro`. */
export const MERCADO_PAGO_PAYMENT_TYPE = 'Mercado Pago';

/** Texto claro de la fila, debajo del título (p. ej. "Tarjeta de Crédito - Visa"). */
export function cardRowSubtitle(
  item: Extract<PaymentMethodItem, { kind: 'card' }>,
): string {
  const variant = item.networkVariant.toLowerCase();
  if (variant === 'prepaga') return `Tarjeta prepaga - ${item.network}`;
  return translate('paymentsUI.web.cardKind', {
    variant: item.networkVariant,
    network: item.network,
  });
}

export function toAnalyticsLabels(item: PaymentMethodItem): PaymentAnalyticsLabels | null {
  if (item.kind === 'mercadopago') {
    const paymentBox = translate('paymentsUI.web.mercadoPagoRow');
    return {
      payment_type: MERCADO_PAGO_PAYMENT_TYPE,
      payment_box: paymentBox,
    };
  }

  if (item.kind === 'card') {
    // Paridad con VTEX: solo la red ("Visa"). Versión completa ("Visa Debito"):
    // const fromProvider = item.paymentMethod?.trim();
    // const paymentType =
    //   fromProvider || [item.network, item.networkVariant].filter(Boolean).join(' ').trim();
    const paymentType = item.network?.trim();
    if (!paymentType) return null;
    return {
      payment_type: paymentType,
      payment_box: cardRowSubtitle(item),
      ...(item.issuer ? { payment_issuer: item.issuer } : {}),
    };
  }

  return null;
}

export function analyticsLabelsForMethod(
  methods: PaymentMethodItem[],
  id: string,
): PaymentAnalyticsLabels | null {
  const item = methods.find((method) => method.kind !== 'add' && method.id === id);
  if (!item) return null;
  return toAnalyticsLabels(item);
}
